import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/use-auth-store';
import type { useQueryClient } from '@tanstack/react-query';

/**
 * Connects to the WebSocket gateway and listens for dashboard events.
 * On sale:created or sale:summary, invalidates the dashboard queries
 * so TanStack Query refetches fresh data.
 */
export function useDashboardSocket(queryClient: ReturnType<typeof useQueryClient>) {
  const socketRef = useRef<Socket | null>(null);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (!user) return;

    const socket = io(window.location.origin, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socketRef.current = socket;

    socket.on('sale:created', () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    });

    socket.on('sale:summary', () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    });

    socket.on('dashboard:refresh', () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, queryClient]);
}
